// src/components/manager-v2/ManagerDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users, Clock, Calendar, TrendingUp, Award, CheckCircle,
    AlertCircle, XCircle, DollarSign, FileText, Target, CheckSquare,
    MessageSquare, ClipboardCheck, Briefcase, Network, LogIn, LogOut, Megaphone, Activity, Home, ListTodo
} from 'lucide-react';
import { useLeave } from '../../contexts/LeaveContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import LeaveRequestModal from '../common/LeaveRequestModal';
import WorkFromHomeModal from '../common/WorkFromHomeModal';
import { useAuth } from '../../hooks/useAuth';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useEmployeeUpdates } from '../../hooks/useEmployeeUpdates';
import attendanceService from '../../services/attendance.service';
import leaveService from '../../services/leave.service';
import payrollService from '../../services/payroll.service';
import performanceService from '../../services/performance.service';
import vibeService from '../../services/vibe.service';
import CelebrationHub from '../common/vibe/CelebrationHub';
import PersonalTasksWidget from '../common/tasks/PersonalTasksWidget';
import TeamTasksWidget from '../common/tasks/TeamTasksWidget';
import { getCurrentISTDate, getISTComponents } from '../../utils/dateHelpers';

const ManagerDashboard = () => {
    const { user } = useAuth();
    const { employees, fetchEmployees } = useEmployees();
    const { rawLeaveBalance, requestLeave, fetchLeaveData: refreshLeaveData } = useLeave();
    const { onboardingData } = useOnboarding();
    const navigate = useNavigate();
    const [dashboardView, setDashboardView] = useState('personal'); // 'personal' or 'team'

    const currentEmployee = employees.find(emp => emp.email === user?.email);

    // State for personal dashboard widgets
    const [personalDashboardData, setPersonalDashboardData] = useState({
        attendance: { present: 0, working: 0, total: 0 },
        leaveBalance: { cl: { availed: 0, total: 0 }, sl: { availed: 0, total: 0 }, lop: 0 },
        thisMonth: '--',
        goalsAchieved: '--',
        loading: true
    });

    // State for team dashboard widgets
    const [teamDashboardData, setTeamDashboardData] = useState({
        presentToday: '--',
        onLeave: '--',
        pendingApprovals: '--',
        loading: true
    });

    // State for Quick Actions
    const [punchStatus, setPunchStatus] = useState({
        isPunchedIn: false,
        lastPunchTime: null,
        loading: false
    });

    // State for Announcements
    const [announcements, setAnnouncements] = useState([]);
    const [announcementsLoading, setAnnouncementsLoading] = useState(true);

    // State for Badge Counts
    const [badgeCounts, setBadgeCounts] = useState({
        taskbox: 0,
        vibe: 0,
        teamTaskbox: 0,
        teamApprovals: 0
    });

    // State for Vibe Visit Tracking
    const [lastVibeVisit, setLastVibeVisit] = useState(() => {
        const saved = localStorage.getItem('managerLastVibeVisit');
        return saved ? new Date(saved) : null;
    });

    // State for Active Session Timer
    const [activeTimer, setActiveTimer] = useState(0);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const timerRef = useRef(null);

    // State for current date and time
    const [currentDate, setCurrentDate] = useState(new Date());

    // State for clock format (true = 24-hour, false = 12-hour)
    const [is24HourFormat, setIs24HourFormat] = useState(true);

    // State for modals
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [showWFHModal, setShowWFHModal] = useState(false);

    // Listen for employee updates
    useEmployeeUpdates((action, data) => {
        console.log('ManagerDashboard received update:', action, data);
        // Force refresh of employees when updates occur
        fetchEmployees(true);
    });

    // Get team members (employees reporting to this manager)
    const teamMembers = employees.filter(emp => emp.managerId === user?.id);

    // Fetch personal dashboard data
    useEffect(() => {
        const fetchPersonalData = async () => {
            if (!user?.id) return;

            try {
                setPersonalDashboardData(prev => ({ ...prev, loading: true }));

                // Fetch all personal data in parallel
                const [attendanceData, leaveData, payrollData, goalsData] = await Promise.allSettled([
                    attendanceService.getMonthlyAttendanceSummary(user), // Pass user profile for accurate working days calculation
                    leaveService.getMonthlyLeaveSummary(),
                    payrollService.getMyPayslips(),
                    performanceService.getEmployeeGoals(user.id)
                ]);

                // Process attendance data
                let attendanceValue = { present: 0, working: 0, total: 0 };
                if (attendanceData.status === 'fulfilled' && attendanceData.value) {
                    const summary = attendanceData.value;
                    attendanceValue = {
                        present: summary.presentDays || 0,
                        working: summary.totalWorkingDays || 0,
                        total: summary.totalDaysInMonth || 0
                    };
                    console.log('📊 Manager Personal Attendance Summary:', summary);
                }

                // Process leave balance data
                let leaveValue = { cl: { availed: 0, total: 0 }, sl: { availed: 0, total: 0 }, lop: 0 };
                if (leaveData.status === 'fulfilled' && leaveData.value) {
                    const leave = leaveData.value;
                    leaveValue = {
                        cl: {
                            availed: leave.cl?.availed || 0,
                            total: leave.cl?.total || 0
                        },
                        sl: {
                            availed: leave.sl?.availed || 0,
                            total: leave.sl?.total || 0
                        },
                        lop: leave.lop || 0
                    };
                }

                // Process payroll data
                let payrollValue = '--';
                if (payrollData.status === 'fulfilled' && payrollData.value) {
                    const payslips = Array.isArray(payrollData.value) ? payrollData.value : payrollData.value.payslips;
                    if (payslips && payslips.length > 0) {
                        const latestPayslip = payslips[0];
                        if (latestPayslip.net_pay !== undefined) {
                            payrollValue = `₹${parseFloat(latestPayslip.net_pay).toLocaleString('en-IN')}`;
                        } else if (latestPayslip.total_earnings !== undefined) {
                            payrollValue = `₹${parseFloat(latestPayslip.total_earnings).toLocaleString('en-IN')}`;
                        }
                    }
                }

                // Process goals data
                let goalsValue = '--';
                if (goalsData.status === 'fulfilled' && goalsData.value) {
                    const goals = Array.isArray(goalsData.value) ? goalsData.value : goalsData.value.goals;
                    if (goals && goals.length > 0) {
                        const completedGoals = goals.filter(g =>
                            g.status === 'completed' || g.progress === 100
                        ).length;
                        goalsValue = `${completedGoals}/${goals.length}`;
                    }
                }

                setPersonalDashboardData({
                    attendance: attendanceValue,
                    leaveBalance: leaveValue,
                    thisMonth: payrollValue,
                    goalsAchieved: goalsValue,
                    loading: false
                });
            } catch (error) {
                console.error('Error fetching personal dashboard data:', error);
                setPersonalDashboardData(prev => ({ ...prev, loading: false }));
            }
        };

        fetchPersonalData();
    }, [user?.id]);

    // Fetch team dashboard data
    useEffect(() => {
        const fetchTeamData = async () => {
            if (!user?.id || teamMembers.length === 0) return;

            try {
                setTeamDashboardData(prev => ({ ...prev, loading: true }));

                // Fetch team attendance for today (use IST date)
                const today = getCurrentISTDate();
                const [teamAttendanceData] = await Promise.allSettled([
                    attendanceService.getTeamAttendance(today)
                ]);

                // Process team attendance
                let presentValue = '--';
                let onLeaveValue = '--';
                if (teamAttendanceData.status === 'fulfilled' && teamAttendanceData.value) {
                    const attendance = Array.isArray(teamAttendanceData.value)
                        ? teamAttendanceData.value
                        : teamAttendanceData.value.attendance;
                    if (attendance) {
                        const present = attendance.filter(a => a.status === 'present' || a.check_in_time).length;
                        const onLeave = attendance.filter(a => a.status === 'leave' || a.on_leave).length;
                        presentValue = `${present}/${teamMembers.length}`;
                        onLeaveValue = onLeave.toString();
                    }
                }

                setTeamDashboardData({
                    presentToday: presentValue,
                    onLeave: onLeaveValue,
                    pendingApprovals: '--', // This would need approval service
                    loading: false
                });
            } catch (error) {
                console.error('Error fetching team dashboard data:', error);
                setTeamDashboardData(prev => ({ ...prev, loading: false }));
            }
        };

        fetchTeamData();
    }, [user?.id, teamMembers.length]);

    // Fetch punch status
    useEffect(() => {
        const fetchPunchStatus = async () => {
            if (!user?.id) return;

            try {
                const response = await attendanceService.getTodayPunches();

                // API returns {success, punches, lastPunchType, canCheckIn, canCheckOut}
                if (response && response.success) {
                    const isPunchedIn = response.lastPunchType === 'check_in';

                    // Find the most recent check-in for active session calculation
                    let lastCheckIn = null;
                    if (response.punches && Array.isArray(response.punches) && response.punches.length > 0) {
                        // Filter for check-ins and get the most recent one
                        const checkIns = response.punches.filter(p =>
                            p.punch_type === 'check_in' || p.type === 'check_in'
                        );
                        if (checkIns.length > 0) {
                            lastCheckIn = checkIns[checkIns.length - 1];
                        }
                    }

                    setPunchStatus({
                        isPunchedIn,
                        lastPunchTime: lastCheckIn?.punch_time || lastCheckIn?.created_at || null,
                        loading: false
                    });

                    // Start timer only if currently checked in, using the last check-in time
                    if (isPunchedIn && lastCheckIn) {
                        const checkInTime = new Date(lastCheckIn.punch_time || lastCheckIn.created_at);
                        if (!isNaN(checkInTime.getTime())) {
                            const now = new Date();
                            const elapsed = Math.max(0, Math.floor((now.getTime() - checkInTime.getTime()) / 1000));
                            setActiveTimer(elapsed);
                            setIsTimerActive(true);
                        }
                    } else {
                        setIsTimerActive(false);
                        setActiveTimer(0);
                    }
                } else {
                    setPunchStatus({ isPunchedIn: false, lastPunchTime: null, loading: false });
                    setIsTimerActive(false);
                    setActiveTimer(0);
                }
            } catch (error) {
                console.error('Error fetching punch status:', error);
                setPunchStatus({ isPunchedIn: false, lastPunchTime: null, loading: false });
            }
        };

        fetchPunchStatus();

        // Also refetch when component becomes visible (user navigates back)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchPunchStatus();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user?.id]);

    // Fetch announcements
    useEffect(() => {
        const fetchAnnouncements = async () => {
            if (!user?.id) return;

            try {
                setAnnouncementsLoading(true);
                // Fetch announcements from dedicated announcements API
                const response = await vibeService.getAnnouncements(3);

                // API returns {success, announcements}
                if (response && response.success && response.announcements) {
                    setAnnouncements(response.announcements);
                } else {
                    setAnnouncements([]);
                }
                setAnnouncementsLoading(false);
            } catch (error) {
                console.error('Error fetching announcements:', error);
                setAnnouncements([]);
                setAnnouncementsLoading(false);
            }
        };

        fetchAnnouncements();
    }, [user?.id]);

    // Fetch badge counts
    useEffect(() => {
        const fetchBadgeCounts = async () => {
            try {
                console.log('🔍 Manager Dashboard - Fetching dynamic badge counts...');

                // Fetch personal tasks count
                let personalTaskCount = 0;
                try {
                    const taskService = (await import('../../services/task.service')).default;
                    const personalTaskResponse = await taskService.getTasks({
                        category: 'personal',
                        status: 'pending'
                    });
                    personalTaskCount = personalTaskResponse.count || personalTaskResponse.tasks?.length || 0;
                    console.log('📊 Manager Personal Task count for badge:', personalTaskCount);
                } catch (taskError) {
                    console.warn('Manager Dashboard: Could not fetch personal task count:', taskError);
                }

                // Fetch team tasks count
                let teamTaskCount = 0;
                try {
                    const taskService = (await import('../../services/task.service')).default;
                    const teamTaskResponse = await taskService.getTasks({
                        category: 'team_task',
                        status: 'pending'
                    });
                    teamTaskCount = teamTaskResponse.count || teamTaskResponse.tasks?.length || 0;
                    console.log('👥 Manager Team Task count for badge:', teamTaskCount);
                } catch (teamTaskError) {
                    console.warn('Manager Dashboard: Could not fetch team task count:', teamTaskError);
                }

                // Fetch pending approvals count
                let approvalsCount = 0;
                try {
                    const { api } = await import('../../services/api');
                    const approvalsResponse = await api.get('/api/manager/pending-approvals');
                    approvalsCount = approvalsResponse.data.count || approvalsResponse.data.length || 0;
                    console.log('✅ Manager Pending approvals count for badge:', approvalsCount);
                } catch (approvalsError) {
                    console.warn('Manager Dashboard: Could not fetch approvals count:', approvalsError);
                }

                // Calculate total task count (personal + team)
                const totalTaskCount = personalTaskCount + teamTaskCount;

                // Calculate vibe badge count (unseen posts from last 24h)
                let vibeCount = 0;
                try {
                    const vibeFeedResponse = await vibeService.getFeed({
                        filter: 'company',
                        limit: 20,
                        sortBy: 'recent'
                    });

                    // Count posts from last 24 hours that haven't been seen
                    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                    const recentPosts = vibeFeedResponse.posts?.filter(post => {
                        const postDate = new Date(post.created_at);
                        const isRecent = postDate > oneDayAgo;
                        const isUnseen = !lastVibeVisit || postDate > lastVibeVisit;
                        return isRecent && isUnseen;
                    }) || [];

                    vibeCount = recentPosts.length;
                    console.log('🎉 Manager Vibe count for badge:', vibeCount, '(unseen recent posts from last 24h)');
                } catch (vibeError) {
                    console.warn('Manager Dashboard: Could not fetch vibe feed for badge:', vibeError);
                }

                setBadgeCounts({
                    taskbox: totalTaskCount,
                    teamTaskbox: teamTaskCount,
                    teamApprovals: approvalsCount,
                    vibe: vibeCount
                });

                console.log('✅ Manager Dashboard - Updated badge counts:', {
                    taskbox: totalTaskCount,
                    teamTaskbox: teamTaskCount,
                    teamApprovals: approvalsCount,
                    vibe: vibeCount
                });
            } catch (error) {
                console.error('Error fetching Manager dashboard badge counts:', error);
                setBadgeCounts({ taskbox: 0, teamTaskbox: 0, teamApprovals: 0, vibe: 0 });
            }
        };

        // Fetch badge counts initially and when announcements change
        if (user?.id) {
            fetchBadgeCounts();
        }
    }, [user?.id, announcements, lastVibeVisit]);

    // Track Vibe visits
    const handleVibeVisit = () => {
        const now = new Date();
        setLastVibeVisit(now);
        localStorage.setItem('managerLastVibeVisit', now.toISOString());
        console.log('📍 Manager Dashboard: Vibe visit tracked');
    };

    // Timer effect - increment every second when active
    useEffect(() => {
        if (isTimerActive) {
            timerRef.current = setInterval(() => {
                setActiveTimer(prev => prev + 1);
            }, 1000);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isTimerActive]);

    // Update current date and time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentDate(new Date());
        }, 1000); // Update every second for real-time clock

        return () => clearInterval(timer);
    }, []);

    // Format timer to HH:MM:SS
    const formatTimer = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Format date to DD/MM/YYYY (IST)
    const formatDateDisplay = (date) => {
        const { date: istDateStr } = getISTComponents(date);
        const [year, month, day] = istDateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    // Get day of week (IST)
    const getDayOfWeek = (date) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const { dayOfWeek } = getISTComponents(date);
        return days[dayOfWeek];
    };

    // Format time as HH:MM:SS (24-hour IST)
    const formatTime24Hour = (date) => {
        const { hour, minute, second } = getISTComponents(date);
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
    };

    // Format time as HH:MM:SS AM/PM (12-hour IST)
    const formatTime12Hour = (date) => {
        const { hour, minute, second } = getISTComponents(date);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')} ${ampm}`;
    };

    // Get formatted time based on current format preference
    const getFormattedTime = (date) => {
        return is24HourFormat ? formatTime24Hour(date) : formatTime12Hour(date);
    };

    // Toggle clock format
    const toggleClockFormat = () => {
        setIs24HourFormat(prev => !prev);
    };

    // Handle punch in/out
    const handlePunch = async (type) => {
        if (punchStatus.loading) return;

        try {
            setPunchStatus(prev => ({ ...prev, loading: true }));
            // Include shift_id to help backend determine correct attendance_date for overnight shifts
            const shiftId = currentEmployee?.shift_id;
            const response = await attendanceService.punch(type, null, null, shiftId);

            setPunchStatus({
                isPunchedIn: type === 'check_in',
                lastPunchTime: response.punch?.punch_time || new Date().toISOString(),
                loading: false
            });

            // Show verification feedback
            const vm = response.verificationMethod;
            if (type === 'check_in') {
                if (vm === 'wfh') {
                    alert('Working from home today. Punch recorded.');
                } else if (vm === 'none' && response.geofence?.officesConfigured) {
                    alert('Punch recorded but your location could not be verified. It has been flagged for admin review.');
                }
            }

            // Update timer
            if (type === 'check_in') {
                setActiveTimer(0);
                setIsTimerActive(true);
            } else {
                setIsTimerActive(false);
                setActiveTimer(0);
            }

            // Refresh personal dashboard data
            const summary = await attendanceService.getMonthlyAttendanceSummary(user);
            console.log('🔄 Manager Refreshed Attendance Summary after punch:', summary);
            if (summary.presentDays !== undefined) {
                setPersonalDashboardData(prev => ({
                    ...prev,
                    attendance: {
                        present: summary.presentDays || 0,
                        working: summary.totalWorkingDays || 0,
                        total: summary.totalDaysInMonth || 0
                    }
                }));
            }
        } catch (error) {
            console.error('Error punching:', error);
            const errorMsg = error.response?.data?.error || `Failed to punch ${type === 'check_in' ? 'in' : 'out'}. Please try again.`;
            alert(errorMsg);
            setPunchStatus(prev => ({ ...prev, loading: false }));
        }
    };

    // My Access modules for personal view
    const myAccessModules = [
        {
            id: 'taskbox',
            title: 'Task Box',
            icon: CheckSquare,
            bgColor: 'bg-gray-100 dark:bg-gray-700',
            iconColor: 'text-gray-700 dark:text-gray-300',
            route: '/manager/taskbox',
            badge: badgeCounts.taskbox
        },
        {
            id: 'vibe',
            title: 'Vibe',
            icon: MessageSquare,
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            iconColor: 'text-purple-700 dark:text-purple-400',
            route: '/manager/vibe',
            badge: badgeCounts.vibe
        },
        {
            id: 'compensation',
            title: 'Compensation',
            icon: DollarSign,
            bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
            iconColor: 'text-yellow-700 dark:text-yellow-400',
            route: '/manager/payroll'
        },
        {
            id: 'attendance',
            title: 'Attendance',
            icon: Clock,
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            iconColor: 'text-red-700 dark:text-red-400',
            route: '/manager/attendance'
        },
        {
            id: 'documents',
            title: 'Documents',
            icon: FileText,
            bgColor: 'bg-teal-100 dark:bg-teal-900/30',
            iconColor: 'text-teal-700 dark:text-teal-400',
            route: '/manager/documents'
        },
        {
            id: 'performance',
            title: 'Performance',
            icon: TrendingUp,
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            iconColor: 'text-purple-700 dark:text-purple-400',
            route: '/manager/performance'
        },
        {
            id: 'orgview',
            title: 'Org View',
            icon: Network,
            bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
            iconColor: 'text-indigo-700 dark:text-indigo-400',
            route: '/manager/org-view'
        }
    ];

    const handleModuleClick = (route) => {
        // Track Vibe visits
        if (route === '/manager/vibe') {
            handleVibeVisit();
        }
        navigate(route);
    };

    // Team Access modules for team view
    const teamAccessModules = [
        {
            id: 'team-taskbox',
            title: 'Team Task Box',
            icon: ListTodo,
            bgColor: 'bg-gray-100 dark:bg-gray-700',
            iconColor: 'text-gray-700 dark:text-gray-300',
            route: '/manager/team-tasks',
            badge: badgeCounts.teamTaskbox
        },
        {
            id: 'team-attendance',
            title: 'Team Attendance',
            icon: Clock,
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            iconColor: 'text-red-700 dark:text-red-400',
            route: '/manager/team-attendance'
        },
        {
            id: 'my-team',
            title: 'My Team (Employees)',
            icon: Users,
            bgColor: 'bg-blue-100 dark:bg-blue-900/30',
            iconColor: 'text-blue-700 dark:text-blue-400',
            route: '/manager/team-management'
        },
        {
            id: 'team-performance',
            title: 'Team Performance',
            icon: TrendingUp,
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            iconColor: 'text-purple-700 dark:text-purple-400',
            route: '/manager/team-performance'
        },
        {
            id: 'team-approvals',
            title: 'Team Approvals',
            icon: ClipboardCheck,
            bgColor: 'bg-orange-100 dark:bg-orange-900/30',
            iconColor: 'text-orange-700 dark:text-orange-400',
            route: '/manager/team-approvals',
            badge: badgeCounts.teamApprovals
        }
    ];

    // Handle leave request submission
    const handleLeaveRequest = async (formData) => {
        if (!currentEmployee?.id) return;

        try {
            console.log('🔍 Raw leave request data from modal:', formData);

            // Calculate total days
            const calculateLeaveDays = (fromDate, toDate, isHalfDay) => {
                if (!fromDate || !toDate) return 0;
                const start = new Date(fromDate);
                const end = new Date(toDate);
                if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                    return 0;
                }
                const diffTime = end.getTime() - start.getTime();
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                const total = diffDays >= 0 ? diffDays + 1 : 1;
                return isHalfDay ? 0.5 : total;
            };

            const totalDays = calculateLeaveDays(formData.fromDate, formData.toDate, formData.isHalfDay);

            // Use camelCase format as expected by LeaveContext.requestLeave
            const processedData = {
                leaveTypeId: formData.leaveTypeId,
                fromDate: formData.fromDate,
                toDate: formData.toDate,
                days: totalDays,
                reason: formData.reason,
                isHalfDay: formData.isHalfDay,
                emergencyContact: formData.emergencyContact
            };

            console.log('✅ Processed leave data before API call:', processedData);
            const response = await requestLeave(processedData);
            console.log('📋 API response:', response);

            if (response.success) {
                await refreshLeaveData(true);
                setShowLeaveModal(false);
                alert('Leave request submitted successfully!');
            } else {
                alert(response.message || 'Failed to submit leave request');
            }
        } catch (error) {
            console.error('Error submitting leave request:', error);
            alert('An error occurred while submitting leave request');
        }
    };

    // Handle WFH request submission
    const handleWFHSuccess = () => {
        setShowWFHModal(false);
        alert('Work from home request submitted successfully!');
    };

    // Personal Dashboard Stats - now with real-time data
    const personalStats = [
        { icon: Clock, label: 'My Attendance', value: personalDashboardData.attendance, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30', isAttendance: true },
        { icon: FileText, label: 'My Requests', color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30', isMyRequests: true },
        // Active Session widget - only shown when timer is active
        ...(isTimerActive ? [{ icon: Activity, label: 'Active Session', value: formatTimer(activeTimer), color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30', isTimer: true }] : []),
    ];

    // Team Dashboard Stats - now with real-time data
    const teamStats = [
        { icon: Users, label: 'Team Members', value: teamMembers.length, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
        { icon: CheckCircle, label: 'Present Today', value: teamDashboardData.presentToday, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
        { icon: Calendar, label: 'On Leave', value: teamDashboardData.onLeave, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
        { icon: TrendingUp, label: 'Avg Performance', value: '--', color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
    ];

    const currentStats = dashboardView === 'personal' ? personalStats : teamStats;

    return (
        <div className="space-y-6">
            {/* Header with Toggle */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {dashboardView === 'personal' ? 'My Dashboard' : 'Team Dashboard'}
                    </h1>
                    <p className="text-base text-gray-600 dark:text-gray-400 mt-1">
                        {dashboardView === 'personal'
                            ? <>Welcome back, <span className="text-xl font-bold text-green-600 dark:text-green-400">{user?.first_name || user?.name || 'Manager'}</span></>
                            : `Managing ${teamMembers.length} team members`
                        }
                    </p>
                </div>

                {/* Toggle Switch */}
                <div className="flex items-center gap-3 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                    <button
                        onClick={() => setDashboardView('personal')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            dashboardView === 'personal'
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                        Personal View
                    </button>
                    <button
                        onClick={() => setDashboardView('team')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            dashboardView === 'team'
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                        Team View
                    </button>
                </div>
            </div>

            {/* Date and Day Display with Digital Clock */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-3 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                            {formatDateDisplay(currentDate)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {getDayOfWeek(currentDate)}
                        </p>
                    </div>
                    <div className="text-right">
                        <p
                            onClick={toggleClockFormat}
                            className="text-2xl font-bold text-blue-600 dark:text-blue-400 font-mono cursor-pointer hover:text-blue-700 dark:hover:text-blue-300 transition-colors select-none"
                            title="Click to toggle 12/24 hour format"
                        >
                            {getFormattedTime(currentDate)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {is24HourFormat ? '24-hour' : '12-hour'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className={`grid grid-cols-1 md:grid-cols-2 ${dashboardView === 'personal' && isTimerActive ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6`}>
                {currentStats.map((stat, index) => {
                    const Icon = stat.icon;

                    // Render attendance widget for personal view
                    if (stat.isAttendance && dashboardView === 'personal') {
                        return (
                            <div
                                key={index}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                                        <Icon className={`w-6 h-6 ${stat.color}`} />
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    {stat.label}
                                </h3>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                                    {stat.value.present}/{stat.value.working}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Total days in month: {stat.value.total}
                                </p>
                            </div>
                        );
                    }

                    // Render My Requests widget for personal view
                    if (stat.isMyRequests && dashboardView === 'personal') {
                        return (
                            <div
                                key={index}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                                        <Icon className={`w-6 h-6 ${stat.color}`} />
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-4">
                                    {stat.label}
                                </h3>
                                <div className="space-y-3">
                                    <button
                                        onClick={() => setShowLeaveModal(true)}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
                                    >
                                        <Calendar className="w-4 h-4" />
                                        Apply Leaves
                                    </button>
                                    <button
                                        onClick={() => setShowWFHModal(true)}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium text-sm"
                                    >
                                        <Home className="w-4 h-4" />
                                        Apply WFH Request
                                    </button>
                                </div>
                            </div>
                        );
                    }

                    // Render other widgets (Team stats, Active Session, etc.)
                    return (
                        <div
                            key={index}
                            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                                    <Icon className={`w-6 h-6 ${stat.color}`} />
                                </div>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {stat.label}
                            </h3>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                                {stat.value}
                            </p>
                        </div>
                    );
                })}
            </div>

            {/* Quick Actions Section - Personal view only */}
            {dashboardView === 'personal' && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Quick Actions</h2>
                    <div className="flex gap-4">
                        <button
                            onClick={() => handlePunch('check_in')}
                            disabled={punchStatus.isPunchedIn || punchStatus.loading}
                            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                                punchStatus.isPunchedIn
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                    : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                        >
                            <LogIn className="w-5 h-5" />
                            {punchStatus.loading && !punchStatus.isPunchedIn ? 'Checking In...' : 'Check In'}
                        </button>
                        <button
                            onClick={() => handlePunch('check_out')}
                            disabled={!punchStatus.isPunchedIn || punchStatus.loading}
                            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                                !punchStatus.isPunchedIn
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                    : 'bg-red-500 hover:bg-red-600 text-white'
                            }`}
                        >
                            <LogOut className="w-5 h-5" />
                            {punchStatus.loading && punchStatus.isPunchedIn ? 'Checking Out...' : 'Check Out'}
                        </button>
                    </div>
                    {punchStatus.lastPunchTime && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 text-center">
                            Last check: {(() => {
                                const { hour, minute } = getISTComponents(new Date(punchStatus.lastPunchTime));
                                const hour12 = hour % 12 || 12;
                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                return `${hour12}:${String(minute).padStart(2, '0')} ${ampm} IST`;
                            })()}
                        </p>
                    )}
                </div>
            )}

            {/* Vibe Feature Section - shown in both views */}
            <div className="mb-6">
                {/* Vibe Header with Navigation */}
                <div
                    className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg p-3 transition-colors"
                    onClick={() => {
                        handleVibeVisit();
                        navigate('/manager/vibe');
                    }}
                >
                    <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-2">
                        <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Vibe</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Company announcements and celebrations</p>
                    </div>
                    <div className="text-purple-600 dark:text-purple-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>

                {/* Vibe Widgets Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Announcements Widget */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow h-[400px] flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2">
                                <Megaphone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Announcements</h3>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/manager/vibe');
                                }}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                            >
                                View All
                            </button>
                        </div>

                        <div className="flex-1 overflow-hidden">
                            {announcementsLoading ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : announcements.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                                    <Megaphone className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No announcements at the moment</p>
                                </div>
                            ) : (
                                <div className="p-4 space-y-3 overflow-y-auto h-full">
                                    {announcements.map((announcement) => (
                                        <div
                                            key={announcement.id}
                                            className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                                            onClick={() => navigate('/manager/vibe')}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                                                    <Megaphone className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                            {announcement.title}
                                                        </h4>
                                                        {announcement.priority === 'high' && (
                                                            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded flex-shrink-0">
                                                                Important
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                                                        {announcement.message}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        {(() => {
                                                            const { date } = getISTComponents(new Date(announcement.created_at));
                                                            const [year, month, day] = date.split('-');
                                                            return `${day}/${month}/${year}`;
                                                        })()} · {announcement.created_by_name || 'Admin'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Let's Celebrate Widget */}
                    <div className="h-[400px]">
                        <CelebrationHub
                            theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                            onPostCreated={() => {
                                // Optional: Refresh or handle post creation
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* TaskBox Feature Section - shown in both views */}
            <div className="mb-6">
                {/* TaskBox Header with Navigation */}
                <div
                    className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg p-3 transition-colors"
                    onClick={() => navigate('/manager/taskbox')}
                >
                    <div className="bg-green-100 dark:bg-green-900 p-2 rounded-lg">
                        <CheckSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Task Box</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Personal and team task management</p>
                    </div>
                    <div className="text-green-600 dark:text-green-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>

                {/* TaskBox Widgets Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Personal Tasks Widget */}
                    <PersonalTasksWidget
                        userRole="manager"
                        dashboardView="personal"
                    />

                    {/* Team Tasks Widget */}
                    <TeamTasksWidget
                        userRole="manager"
                        dashboardView="team"
                    />
                </div>
            </div>

            {/* Personal View Content */}
            {dashboardView === 'personal' && (
                <div className="space-y-6">
                    {/* My Access Section */}
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">My Access</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {myAccessModules.map((module) => {
                                const Icon = module.icon;
                                return (
                                    <div
                                        key={module.id}
                                        onClick={() => handleModuleClick(module.route)}
                                        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                                    >
                                        <div className="p-6 flex flex-col items-center">
                                            <div className={`${module.bgColor} p-4 rounded-2xl mb-3 group-hover:scale-110 transition-transform`}>
                                                <Icon className={`w-10 h-10 ${module.iconColor}`} />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                                                {module.title}
                                            </span>
                                            {(module.badge && Number(module.badge) > 0 && !isNaN(module.badge) && module.badge !== '0' && module.badge !== 0) ? (
                                                <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                                                    {module.badge}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Team View Content */}
            {dashboardView === 'team' && (
                <div className="space-y-6">
                    {/* Team Access Section */}
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Team Access</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
                            {teamAccessModules.map((module) => {
                                const Icon = module.icon;
                                return (
                                    <div
                                        key={module.id}
                                        onClick={() => handleModuleClick(module.route)}
                                        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                                    >
                                        <div className="p-6 flex flex-col items-center">
                                            <div className={`${module.bgColor} p-4 rounded-2xl mb-3 group-hover:scale-110 transition-transform`}>
                                                <Icon className={`w-10 h-10 ${module.iconColor}`} />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                                                {module.title}
                                            </span>
                                            {(module.badge && Number(module.badge) > 0 && !isNaN(module.badge) && module.badge !== '0' && module.badge !== 0) ? (
                                                <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                                                    {module.badge}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Leave Request Modal */}
            {showLeaveModal && (
                <LeaveRequestModal
                    leaveTypes={rawLeaveBalance?.balance || rawLeaveBalance?.balances || []}
                    onClose={() => setShowLeaveModal(false)}
                    onSubmit={handleLeaveRequest}
                />
            )}

            {/* Work From Home Modal */}
            {showWFHModal && (
                <WorkFromHomeModal
                    isOpen={showWFHModal}
                    onClose={() => setShowWFHModal(false)}
                    managerId={currentEmployee?.manager_id}
                    onSubmitSuccess={handleWFHSuccess}
                />
            )}
        </div>
    );
};

export default ManagerDashboard;
